import { ReactElement, isValidElement, useMemo } from "react";
import { ScoreIndicator } from "src/components/ScoreIndicator";
import { formatDuration } from "src/utils/duration";
import { Statistic, Progress, Tag, Badge } from "antd";
import { EntityTypes } from "@vzbl/integrations";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  FieldTimeOutlined,
  NumberOutlined,
  PercentageOutlined,
  DollarOutlined,
  StarOutlined,
  TeamOutlined,
  BranchesOutlined,
  CodeOutlined,
  PullRequestOutlined,
} from "@ant-design/icons";

// Define the possible metric display types
export type MetricDisplayType =
  | "statistic" // Use Statistic component for numbers with labels
  | "progress" // Use Progress component for percentage/score displays
  | "tag" // Use Tag component for status/classifications
  | "badge"; // Use Badge component for status indicators

// Define the possible metric types with their default display components
export type MetricType =
  | "score" // Progress component (0-100)
  | "classification" // Tag component with colors
  | "urgency" // Tag component with colors
  | "duration" // Statistic with time icon
  | "number" // Statistic component
  | "percentage" // Progress or Statistic with %
  | "currency" // Statistic with currency symbol
  | "size" // Statistic with size icon
  | "count"; // Statistic with count icon

// Define display configuration for metrics
export interface MetricDisplayConfig {
  type: MetricDisplayType;
  icon?: ReactElement;
  color?: string;
  precision?: number;
  prefix?: ReactElement | string;
  suffix?: ReactElement | string;
}

// Map metric types to their default display configuration
export const defaultDisplayConfig: Record<MetricType, MetricDisplayConfig> = {
  score: {
    type: "progress",
    color: "#1890ff",
  },
  classification: {
    type: "tag",
  },
  urgency: {
    type: "tag",
  },
  duration: {
    type: "statistic",
    icon: <ClockCircleOutlined />,
  },
  number: {
    type: "statistic",
    icon: <NumberOutlined />,
  },
  percentage: {
    type: "statistic",
    icon: <PercentageOutlined />,
    suffix: "%",
    precision: 1,
  },
  currency: {
    type: "statistic",
    icon: <DollarOutlined />,
    precision: 2,
  },
  size: {
    type: "statistic",
    icon: <StarOutlined />,
  },
  count: {
    type: "statistic",
    icon: <TeamOutlined />,
  },
};

export interface DynamicMetrics {
  key: string;
  label: string;
  value: string | number | ReactElement;
  preciseValue?: string; // Full precision value for tooltip display
  tooltip?: string;
  category: "analysis" | "process" | "effort" | "workflow";
  priority: number;
  showIfEmpty?: boolean;
  type: MetricType;
  displayConfig?: Partial<MetricDisplayConfig>;
  order?: number;
}

// Helper function to format explanation text
const formatExplanation = (explanation: any): string => {
  if (!explanation) return "No explanation available";
  if (Array.isArray(explanation)) return explanation.join("\n");
  if (typeof explanation === "string") return explanation;
  return String(explanation);
};
//we also want to add other metrics from other entities like pr cycle time, commit lead time, status changes, assignees

// Define metric configuration type
interface MetricConfig {
  key: string;
  showIfEmpty: boolean;
  type: MetricType;
}

// Define metric categories and their priorities
const metricCategories: Record<
  string,
  {
    priority: number;
    metrics: MetricConfig[];
  }
> = {
  process: {
    priority: 900,
    metrics: [
      { key: "vzblResolutionTime", showIfEmpty: false, type: "duration" },
      { key: "vzblCycleTime", showIfEmpty: false, type: "duration" },
      { key: "vzblLeadTime", showIfEmpty: false, type: "duration" },
    ],
  },
  effort: {
    priority: 800,
    metrics: [
      { key: "vzblStoryPoints", showIfEmpty: false, type: "size" },
      { key: "vzblTimeSpent", showIfEmpty: false, type: "duration" },
      { key: "vzblOriginalEstimate", showIfEmpty: false, type: "duration" },
      { key: "vzblRemainingEstimate", showIfEmpty: false, type: "duration" },
    ],
  },
  analysis: {
    priority: 950,
    metrics: [
      { key: "vzblClassification", showIfEmpty: true, type: "classification" },
      { key: "vzblUrgency", showIfEmpty: true, type: "urgency" },
      { key: "vzblImpact", showIfEmpty: true, type: "classification" },
      { key: "vzblClarityScore", showIfEmpty: true, type: "score" },
      { key: "vzblFrictionScore", showIfEmpty: true, type: "score" },
    ],
  },
  workflow: {
    priority: 700,
    metrics: [
      { key: "vzblAge", showIfEmpty: false, type: "duration" },
      { key: "vzblClockCycleTime", showIfEmpty: false, type: "duration" },
      { key: "vzblClockLeadTime", showIfEmpty: false, type: "duration" },
    ],
  },
};

//age, resolution time, cycle time, lead time, story points, time spent, original estimate, remaining estimate, clock cycle time, clock lead time,
//clcok cycle time, clock lead time, classification, urgency, clarity score, friction score

// Helper function to check if a value should be shown
const shouldShowMetric = (
  key: string,
  value: any,
  showIfEmpty: boolean
): boolean => value || showIfEmpty;

// Helper function to get metric configuration
const getMetricConfig = (key: string): MetricConfig | undefined => {
  for (const category of Object.values(metricCategories)) {
    const metric = category.metrics.find((m) => m.key === key);
    if (metric) {
      return metric;
    }
  }
  return undefined;
};

// Define entity-specific metric rules
export interface MetricRule {
  key: string;
  label: string;
  category: "analysis" | "process" | "effort" | "workflow";
  priority: number;
  order?: number; // Lower numbers appear first within their category
  showIfEmpty: boolean;
  type: MetricType;
  displayConfig?: Partial<MetricDisplayConfig>;
  valueFormatter?: (value: any) => string | number | ReactElement;
  tooltip?: string | ((properties: any) => string);
}

// Default metric rules by entity type
export const defaultMetricRules: Record<string, MetricRule[]> = {
  process: [
    {
      key: "age",
      label: "Age",
      category: "process",
      priority: 900,
      order: 1,
      showIfEmpty: false,
      type: "duration",
      valueFormatter: (value) => formatDuration(value, { style: "human" }),
      tooltip: (props) => `Time since issue was created`,
    },
    {
      key: "resolutionTime",
      label: "Resolution Time",
      category: "process",
      priority: 890,
      order: 2,
      showIfEmpty: false,
      type: "duration",
      valueFormatter: (value) => formatDuration(value, { style: "human" }),
      tooltip: (props) =>
        `Time from when the issue was created to when it was resolved`,
    },
    {
      key: "vzblCycleTime",
      label: "Cycle Time",
      category: "process",
      priority: 880,
      order: 3,
      showIfEmpty: false,
      type: "duration",
      valueFormatter: (value) => formatDuration(value, { style: "human" }),
      tooltip: (props) =>
        `Time from when work started to when it was completed`,
    },
    {
      key: "vzblLeadTime",
      label: "Lead Time",
      category: "process",
      priority: 870,
      order: 4,
      showIfEmpty: false,
      type: "duration",
      valueFormatter: (value) => formatDuration(value, { style: "human" }),
      tooltip: (props) => `Total time from creation to completion`,
    },
  ],
  effort: [
    {
      key: "storyPoints",
      label: "Story Points",
      category: "effort",
      priority: 800,
      order: 1,
      showIfEmpty: false,
      type: "size",
      tooltip: (props) => `Estimated complexity/effort in story points`,
    },
    {
      key: "timeSpent",
      label: "Time Spent",
      category: "effort",
      priority: 790,
      order: 2,
      showIfEmpty: false,
      type: "duration",
      valueFormatter: (value) => formatDuration(value, { style: "human" }),
      tooltip: (props) => `Actual time spent working on this issue`,
    },
    {
      key: "originalEstimate",
      label: "Original Estimate",
      category: "effort",
      priority: 780,
      order: 3,
      showIfEmpty: false,
      type: "duration",
      valueFormatter: (value) => formatDuration(value, { style: "human" }),
      tooltip: (props) => `Initial time estimate for this issue`,
    },
    {
      key: "remainingEstimate",
      label: "Remaining Estimate",
      category: "effort",
      priority: 770,
      order: 4,
      showIfEmpty: false,
      type: "duration",
      valueFormatter: (value) => formatDuration(value, { style: "human" }),
      tooltip: (props) => `Estimated time remaining to complete this issue`,
    },
  ],
  analysis: [
    {
      key: "vzblClassification",
      label: "Classification",
      category: "analysis",
      priority: 950,
      order: 1,
      showIfEmpty: true,
      type: "classification",
      tooltip: (props) =>
        props.vzblClassificationExplanation ||
        "Based on the issue details we have classified this issue",
    },
    {
      key: "vzblUrgency",
      label: "Urgency",
      category: "analysis",
      priority: 940,
      order: 2,
      showIfEmpty: true,
      type: "urgency",
      tooltip: (props) =>
        props.vzblUrgencyExplanation ||
        "Based on issue details we have calculated the urgency of this issue",
    },
    {
      key: "vzblClarityScore",
      label: "Clarity",
      category: "analysis",
      priority: 930,
      order: 3,
      showIfEmpty: true,
      type: "score",
      tooltip: (props) => props.vzblClarityExplanation || "Issue clarity score",
    },
    {
      key: "vzblFrictionScore",
      label: "Complexity",
      category: "analysis",
      priority: 920,
      order: 4,
      showIfEmpty: true,
      type: "score",
      tooltip: (props) =>
        props.vzblFrictionExplanation || "Issue complexity score",
    },
  ],
  workflow: [
    {
      key: "vzblClockCycleTime",
      label: "Clock Cycle Time",
      category: "workflow",
      priority: 700,
      order: 1,
      showIfEmpty: false,
      type: "duration",
      valueFormatter: (value) => formatDuration(value, { style: "human" }),
      tooltip: (props) =>
        `The time in business days between when the issue was started and when it was completed`,
    },
    {
      key: "vzblClockLeadTime",
      label: "Clock Lead Time",
      category: "workflow",
      priority: 690,
      order: 2,
      showIfEmpty: false,
      type: "duration",
      valueFormatter: (value) => formatDuration(value, { style: "human" }),
      tooltip: (props) =>
        `The time in business days between when the issue was created and when it was completed`,
    },
  ],
};

export interface MetricsConfig {
  rules?: MetricRule[];
  categories?: {
    name: string;
    patterns: string[];
    priority: number;
  }[];
  hiddenMetrics?: string[];
}

// Filter rules based on entity type
const getEntityRules = (
  entityType: string,
  allDefaultRules: MetricRule[],
  config?: MetricsConfig
): MetricRule[] => {
  let entityRules: MetricRule[];

  switch (entityType) {
    case EntityTypes.Issue: {
      // Include all process, effort, analysis, and workflow metrics for issues
      entityRules = allDefaultRules;
      break;
    }
    case EntityTypes.PullRequest: {
      // For PRs, include specific metrics
      entityRules = allDefaultRules.filter(
        (rule) =>
          rule.key.startsWith("vzbl") || // Include all vzbl metrics
          ["cycleTime", "leadTime", "timeSpent"].includes(rule.key)
      );
      break;
    }
    case EntityTypes.Commit: {
      // For commits, include only specific metrics
      entityRules = allDefaultRules.filter(
        (rule) => rule.category === "workflow" || rule.key === "timeSpent"
      );
      break;
    }
    default: {
      entityRules = allDefaultRules;
      break;
    }
  }

  // Add any additional rules from config
  return config?.rules ? [...entityRules, ...config.rules] : entityRules;
};

// Updated getAvailableMetrics function
export function getAvailableMetrics(
  entity: { labels: string[]; properties: Record<string, any> },
  config?: MetricsConfig
): DynamicMetrics[] {
  const entityType = entity.labels[0];
  const allDefaultRules = Object.values(defaultMetricRules).flat();
  const rules = getEntityRules(entityType, allDefaultRules, config);

  return rules
    .filter((rule: MetricRule) => {
      // Skip hidden metrics
      if (config?.hiddenMetrics?.includes(rule.key)) {
        return false;
      }

      // Check if value exists and should be shown
      const value = entity.properties[rule.key];
      return shouldShowMetric(rule.key, value, rule.showIfEmpty);
    })
    .map((rule: MetricRule) => {
      const value = entity.properties[rule.key];
      let formattedValue = value;
      let preciseValue: string | undefined;

      // For duration metrics, ensure we have a number and set precise value
      if (rule.type === "duration") {
        const durationValue =
          typeof value === "string" ? Number.parseFloat(value) : value;
        if (typeof durationValue === "number" && !Number.isNaN(durationValue)) {
          preciseValue = formatDuration(durationValue, {
            style: "long",
            maxParts: 6,
            includeSeconds: true,
          });
          formattedValue = formatDuration(durationValue, { style: "human" });
        }
      } else if (rule.valueFormatter) {
        // For non-duration metrics, use the value formatter if provided
        formattedValue = rule.valueFormatter(value);
      }

      // Generate tooltip if provided
      let tooltip = rule.tooltip;
      if (typeof tooltip === "function") {
        tooltip = tooltip(entity.properties);
      }

      return {
        key: rule.key,
        label: rule.label,
        value: formattedValue,
        preciseValue,
        tooltip,
        category: rule.category,
        priority: rule.priority,
        order: rule.order,
        type: rule.type,
        displayConfig: rule.displayConfig,
        showIfEmpty: rule.showIfEmpty,
      } as DynamicMetrics;
    });
}

export function selectMetrics(
  entity: { labels: string[]; properties: Record<string, any> },
  count: number = 3
): DynamicMetrics[] {
  const allMetrics = getAvailableMetrics(entity);
  return allMetrics
    .sort((a: DynamicMetrics, b: DynamicMetrics) => b.priority - a.priority)
    .slice(0, count);
}

// Helper function to get display configuration for a metric
export const getMetricDisplayConfig = (
  metric: DynamicMetrics
): MetricDisplayConfig => {
  const defaultConfig = defaultDisplayConfig[metric.type];
  return {
    ...defaultConfig,
    ...metric.displayConfig,
  };
};

// Helper function to render a metric with appropriate AntD component
export const renderMetric = (metric: DynamicMetrics) => {
  const config = getMetricDisplayConfig(metric);

  switch (config.type) {
    case "statistic": {
      // If value is a ReactElement, return it directly
      if (isValidElement(metric.value)) {
        return metric.value;
      }

      let value: string | number = metric.value as string | number;
      // Format duration values
      if (metric.type === "duration" && typeof value === "number") {
        value = formatDuration(value, { style: "human" });
      } else if (typeof value === "number") {
        value = value.toLocaleString();
      } else {
        value = String(value);
      }

      return (
        <Statistic
          title={metric.label}
          value={value}
          prefix={config.prefix}
          suffix={config.suffix}
          precision={config.precision}
        />
      );
    }

    case "progress": {
      return (
        <Progress
          type="circle"
          percent={Number(metric.value)}
          format={(percent) => `${metric.label}: ${percent}%`}
          strokeColor={config.color}
        />
      );
    }

    case "tag": {
      return (
        <Tag color={config.color}>
          {config.prefix}
          {metric.value}
          {config.suffix}
        </Tag>
      );
    }

    case "badge": {
      return (
        <Badge
          status={config.color as any}
          text={`${metric.label}: ${metric.value}`}
        />
      );
    }

    default: {
      return String(metric.value);
    }
  }
};

// Create a custom hook for the excluded and top properties
export const useMetricProperties = () => {
  const excludedProperties = useMemo(
    () => ({
      // ... your excluded properties here
    }),
    []
  );

  const topProperties = useMemo(
    () => {
      // ... your top properties logic here
    },
    [
      /* add your dependencies here */
    ]
  );

  return { excludedProperties, topProperties };
};
